import React, { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { useCategorySEO } from "../hooks/useSEOData";
import { PostCard } from "../components/PostCard";
import { useFetch } from "../hooks/useFetch";
import { API_BASE_URL } from "../utils/api";
import { Breadcrumbs } from "../components/Breadcrumbs";

export const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Controles UI
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "latest");
  const [viewMode, setViewMode] = useState(searchParams.get("view") || "grid");
  const postsPerPage = 6;

  // URL para posts de la categoría (usamos slug directamente)
  const postsURL = useMemo(() => {
    if (!slug) return null;
    return `${API_BASE_URL}/api/posts.php?action=list&category=${slug}&page=${currentPage}&limit=${postsPerPage}`;
  }, [slug, currentPage, postsPerPage]);

  const {
    data: postsResponse,
    loading: postsLoading,
    error: postsError,
  } = useFetch(postsURL, [slug, currentPage]);

  // Tu useFetch devuelve el array directo, no el objeto completo
  const rawPosts = Array.isArray(postsResponse) ? postsResponse : [];

  const normalized = useMemo(
    () =>
      rawPosts.map((p) => ({
        ...p,
        category: p.category_name || p.category_slug || "",
        author: p.author || p.username || "Red Arauca",
        image: p.image
          ? `${API_BASE_URL}/uploads/${p.image}`
          : `${API_BASE_URL}/uploads/default.jpg`,
      })),
    [rawPosts]
  );

  const sortedPosts = useMemo(() => {
    const arr = [...normalized];
    switch (sortBy) {
      case "views":
        return arr.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
      case "title":
        return arr.sort((a, b) =>
          String(a.title || "").localeCompare(String(b.title || ""), "es", {
            sensitivity: "base",
          })
        );
      case "latest":
      default:
        return arr.sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
    }
  }, [normalized, sortBy]);

  // SOLUCION TEMPORAL: Calcular paginación basándose en el comportamiento de la API
  const pagination = {
    page: currentPage,
    limit: postsPerPage,
    total:
      rawPosts.length >= postsPerPage ? rawPosts.length + 1 : rawPosts.length, // Estimación
    total_pages:
      rawPosts.length >= postsPerPage ? currentPage + 1 : currentPage, // Si hay postsPerPage posts, probablemente hay más páginas
  };

  const categoryData = useMemo(() => {
    if (sortedPosts.length > 0) {
      const p = sortedPosts[0];
      return {
        id: p.category_id,
        name: p.category_name || (slug ? slug.replace(/-/g, " ") : ""),
        slug: p.category_slug || slug || "",
        description: `Todas las publicaciones de ${
          p.category_name || (slug ? slug.replace(/-/g, " ") : "la categoría")
        }`,
        color: getCategoryColor(p.category_slug || slug),
        icon: getCategoryIcon(p.category_slug || slug),
      };
    }
    return {
      id: null,
      name: slug ? slug.replace(/-/g, " ") : "Categoría",
      slug: slug || "categoria",
      description: "Explora todas las publicaciones de esta categoría",
      color: getCategoryColor(slug),
      icon: getCategoryIcon(slug),
    };
  }, [sortedPosts, slug]);

  const seoData = useCategorySEO(categoryData);

  const updateURLParams = (page, srt, view) => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set("page", String(page));
    if (srt !== "latest") newParams.set("sort", srt);
    if (view !== "grid") newParams.set("view", view);
    setSearchParams(newParams, { replace: true });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateURLParams(page, sortBy, viewMode);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    updateURLParams(currentPage, value, viewMode);
  };

  const handleViewChange = (mode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    updateURLParams(currentPage, sortBy, mode);
  };

  if (postsLoading && currentPage === 1) {
    return (
      <>
        <SEOHead title="Cargando categoría..." description="Cargando" noindex />
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Cargando noticias...</span>
          </div>
        </div>
      </>
    );
  }

  if (postsError) {
    return (
      <>
        <SEOHead title="Error" description="No se pudo cargar" noindex />
        <div className="error-container">
          <div className="error-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2>Error al cargar la categoría</h2>
            <p>Hubo un problema al cargar las noticias de esta categoría.</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
            >
              <i className="fas fa-redo"></i>
              Intentar de nuevo
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          image={seoData.image}
          url={seoData.url}
          type={seoData.type}
          article={seoData.article}
        />
      )}

      <main className="category-page">
        {/* Nuevo Header con imagen de fondo */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <h1 className="display-4 fw-bold mb-0 mt-4">
                Categoría: {categoryData.name}
              </h1>

              {/* Breadcrumbs dentro del header */}
              <div className="breadcrumbs-section-header">
                <Breadcrumbs
                  items={[
                    { name: "Inicio", url: "/" },
                    { name: "Categorías", url: "/categorias" },
                    { name: categoryData.name },
                  ]}
                />
              </div>

              {/* Stats de la categoría */}
              <div className="category-stats-header">
                <span className="posts-count-header">
                  <i className={categoryData.icon}></i>
                  {sortedPosts.length}{" "}
                  {sortedPosts.length === 1 ? "publicación" : "publicaciones"}
                </span>
                {currentPage > 1 && (
                  <span className="page-indicator-header">
                    <i className="fas fa-file-alt"></i>
                    Página {currentPage}
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          {/* Controles y filtros */}
          <section className="category-controls">
            <div className="controls-left">
              <div className="sort-controls">
                <label className="sort-label">
                  <i className="fas fa-sort"></i>
                  Ordenar por:
                </label>
                <select
                  value={sortBy}
                  onChange={handleSortChange}
                  className="sort-select"
                >
                  <option value="latest">Más recientes</option>
                  <option value="views">Más vistas</option>
                  <option value="title">Título A-Z</option>
                </select>
              </div>

              {sortedPosts.length > 0 && (
                <div className="results-info">
                  Mostrando {sortedPosts.length} resultados
                </div>
              )}
            </div>

            <div className="view-controls">
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => handleViewChange("grid")}
                title="Vista de cuadrícula"
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => handleViewChange("list")}
                title="Vista de lista"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </section>

          {/* Contenido de posts */}
          <section className="posts-section">
            {sortedPosts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-newspaper"></i>
                </div>
                <h3>No hay publicaciones en esta categoría</h3>
                <p>
                  Aún no se han publicado noticias en esta categoría. Vuelve
                  pronto para ver las últimas novedades.
                </p>
              </div>
            ) : (
              <>
                <div className={`posts-container ${viewMode}`}>
                  {sortedPosts.map((post, index) => (
                    <PostCard
                      key={`${post.id}-${post.slug}-${index}`}
                      post={post}
                      variant={
                        index === 0 && currentPage === 1 && viewMode === "grid"
                          ? "featured"
                          : viewMode === "list"
                          ? "horizontal"
                          : "default"
                      }
                    />
                  ))}
                </div>

                {/* Paginación completa */}
                {pagination?.total_pages > 1 && (
                  <nav
                    className="pagination"
                    aria-label="Navegación de páginas"
                  >
                    {/* Información de paginación */}
                    <div className="pagination-info">
                      <span>
                        Página {currentPage} de {pagination.total_pages}
                        {pagination.total &&
                          ` • ${pagination.total} publicaciones total`}
                      </span>
                    </div>

                    <div className="pagination-controls">
                      {/* Ir a primera página */}
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1 || postsLoading}
                        className="pagination-btn first"
                        title="Primera página"
                      >
                        <i className="fas fa-angle-double-left"></i>
                        <span>Primera</span>
                      </button>

                      {/* Página anterior */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || postsLoading}
                        className="pagination-btn prev"
                        title="Página anterior"
                      >
                        <i className="fas fa-chevron-left"></i>
                        <span>Anterior</span>
                      </button>

                      {/* Números de página con lógica completa */}
                      <div className="pagination-numbers">
                        {getPaginationNumbers(
                          currentPage,
                          pagination.total_pages
                        ).map((item, index) => {
                          if (item === "...") {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="pagination-ellipsis"
                              >
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={item}
                              onClick={() => handlePageChange(item)}
                              className={`pagination-number ${
                                item === currentPage ? "active" : ""
                              }`}
                              disabled={item === currentPage || postsLoading}
                              title={`Ir a página ${item}`}
                            >
                              {item}
                            </button>
                          );
                        })}
                      </div>

                      {/* Página siguiente */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={
                          currentPage === pagination.total_pages || postsLoading
                        }
                        className="pagination-btn next"
                        title="Página siguiente"
                      >
                        <span>Siguiente</span>
                        <i className="fas fa-chevron-right"></i>
                      </button>

                      {/* Ir a última página */}
                      <button
                        onClick={() => handlePageChange(pagination.total_pages)}
                        disabled={
                          currentPage === pagination.total_pages || postsLoading
                        }
                        className="pagination-btn last"
                        title="Última página"
                      >
                        <span>Última</span>
                        <i className="fas fa-angle-double-right"></i>
                      </button>
                    </div>

                    {/* Ir a página específica */}
                    <div className="pagination-jump">
                      <label>
                        Ir a página:
                        <input
                          type="number"
                          min="1"
                          max={pagination.total_pages}
                          value={currentPage}
                          onChange={(e) => {
                            const page = parseInt(e.target.value);
                            if (page >= 1 && page <= pagination.total_pages) {
                              handlePageChange(page);
                            }
                          }}
                          className="pagination-input"
                        />
                      </label>
                    </div>
                  </nav>
                )}
              </>
            )}
          </section>
        </div>

        <style jsx>{`
          .category-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          /* Nuevo estilo para el header con imagen */
          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.6) 100%
              ),
              url("https://img.freepik.com/free-photo/people-connected-social-media_23-2149160160.jpg");
            background-size: cover;
            background-position: center;
            color: white;
            height: 220px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding-left: 40px;
            padding-right: 20px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 600px;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
            opacity: 0.95;
            line-height: 1.2;
          }

          /* Breadcrumbs dentro del header */
          .breadcrumbs-section-header {
            margin: 16px 0 8px 0;
          }

          .breadcrumbs-section-header nav {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-container {
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-link {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header .breadcrumb-link:hover {
            color: white !important;
          }

          .breadcrumbs-section-header .breadcrumb-current {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .breadcrumbs-section-header .breadcrumb-separator {
            color: rgba(255, 255, 255, 0.6) !important;
          }

          /* Stats de categoría en el header */
          .category-stats-header {
            display: flex;
            gap: 16px;
            margin-top: 12px;
            flex-wrap: wrap;
          }

          .posts-count-header,
          .page-indicator-header {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.9rem;
            font-weight: 600;
            padding: 6px 12px;
            border-radius: 20px;
            background: rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.3);
          }

          .posts-count-header {
            color: rgba(255, 255, 255, 0.95);
          }

          .page-indicator-header {
            color: rgba(255, 255, 255, 0.95);
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 20px;
          }

          .loading-container,
          .error-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 50vh;
            padding: 40px;
          }

          .loading-spinner {
            display: flex;
            align-items: center;
            gap: 12px;
            background: white;
            padding: 24px 32px;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            font-weight: 600;
            color: #374151;
          }

          .loading-spinner i {
            color: #3b82f6;
            font-size: 1.2rem;
          }

          .error-content {
            text-align: center;
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 400px;
          }

          .error-icon {
            width: 64px;
            height: 64px;
            background: #fef2f2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px;
            color: #dc2626;
            font-size: 1.5rem;
          }

          .error-content h2 {
            color: #1f2937;
            margin-bottom: 8px;
          }

          .error-content p {
            color: #6b7280;
            margin-bottom: 24px;
          }

          .retry-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: #dc2626;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.2s;
          }

          .retry-btn:hover {
            background: #b91c1c;
            transform: translateY(-1px);
          }

          .category-controls {
            background: white;
            border-radius: 12px;
            padding: 20px;
            margin: 24px 0;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            border: 1px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
          }

          .controls-left {
            display: flex;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;
          }

          .sort-controls {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .sort-label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-weight: 600;
            color: #374151;
            font-size: 0.9rem;
          }

          .sort-select {
            padding: 8px 12px;
            border: 2px solid #d1d5db;
            border-radius: 8px;
            background: white;
            color: #374151;
            font-size: 0.9rem;
            cursor: pointer;
            transition: border-color 0.2s;
          }

          .sort-select:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .results-info {
            color: #6b7280;
            font-size: 0.85rem;
            font-weight: 500;
          }

          .view-controls {
            display: flex;
            gap: 4px;
            background: #f3f4f6;
            padding: 4px;
            border-radius: 8px;
          }

          .view-btn {
            width: 36px;
            height: 36px;
            border: none;
            background: transparent;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s;
          }

          .view-btn:hover {
            background: #e5e7eb;
            color: #374151;
          }

          .view-btn.active {
            background: #3b82f6;
            color: white;
            box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
          }

          .posts-section {
            position: relative;
          }

          .posts-container.grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 24px;
            margin-bottom: 40px;
          }

          .posts-container.list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 40px;
          }

          .empty-state {
            text-align: center;
            padding: 60px 20px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .empty-icon {
            width: 64px;
            height: 64px;
            background: #f3f4f6;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px;
            color: #9ca3af;
            font-size: 1.5rem;
          }

          .empty-state h3 {
            color: #1f2937;
            margin-bottom: 8px;
            font-size: 1.25rem;
          }

          .empty-state p {
            color: #6b7280;
            max-width: 400px;
            margin: 0 auto;
            line-height: 1.5;
          }

          .pagination {
            background: white;
            border-radius: 12px;
            padding: 24px;
            margin-top: 40px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            border: 1px solid #e5e7eb;
          }

          .pagination-info {
            text-align: center;
            margin-bottom: 20px;
            color: #6b7280;
            font-size: 0.9rem;
            font-weight: 500;
          }

          .pagination-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;
            flex-wrap: wrap;
          }

          .pagination-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 10px 16px;
            background: white;
            border: 2px solid #d1d5db;
            border-radius: 8px;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s;
            font-weight: 500;
            font-size: 0.9rem;
          }

          .pagination-btn:hover:not(:disabled) {
            background: #3b82f6;
            border-color: #3b82f6;
            color: white;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
          }

          .pagination-btn:disabled {
            opacity: 0.4;
            cursor: not-allowed;
            transform: none;
          }

          .pagination-btn.first,
          .pagination-btn.last {
            background: #f8fafc;
          }

          .pagination-numbers {
            display: flex;
            gap: 4px;
            margin: 0 8px;
            align-items: center;
          }

          .pagination-number {
            width: 40px;
            height: 40px;
            border: 2px solid #d1d5db;
            background: white;
            border-radius: 8px;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s;
            font-weight: 500;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.9rem;
          }

          .pagination-number:hover:not(:disabled) {
            border-color: #3b82f6;
            color: #3b82f6;
            background: #eff6ff;
            transform: translateY(-1px);
          }

          .pagination-number.active {
            background: #3b82f6;
            border-color: #3b82f6;
            color: white;
            cursor: default;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
          }

          .pagination-number:disabled {
            cursor: default;
          }

          .pagination-ellipsis {
            color: #9ca3af;
            font-weight: 600;
            padding: 0 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 40px;
          }

          .pagination-jump {
            display: flex;
            justify-content: center;
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
          }

          .pagination-jump label {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #6b7280;
            font-size: 0.9rem;
            font-weight: 500;
          }

          .pagination-input {
            width: 80px;
            padding: 6px 10px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            text-align: center;
            font-size: 0.9rem;
            font-weight: 500;
            color: #374151;
            transition: border-color 0.2s;
          }

          .pagination-input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          @media (max-width: 768px) {
            .container {
              padding: 0 16px;
            }

            .page-header {
              height: 180px;
            }

            .page-header h1 {
              font-size: 1.8rem;
            }

            .category-stats-header {
              gap: 8px;
            }

            .posts-count-header,
            .page-indicator-header {
              font-size: 0.8rem;
              padding: 4px 8px;
            }

            .category-controls {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
            }

            .controls-left {
              justify-content: center;
            }

            .view-controls {
              align-self: center;
            }

            .posts-container.grid {
              grid-template-columns: 1fr;
              gap: 16px;
            }

            .pagination {
              flex-wrap: wrap;
              gap: 6px;
              padding: 20px 16px;
            }

            .pagination-controls {
              flex-direction: column;
              gap: 12px;
            }

            .pagination-btn span {
              display: none;
            }

            .pagination-btn {
              min-width: 44px;
              padding: 10px;
            }

            .pagination-btn.first,
            .pagination-btn.last {
              display: none;
            }

            .pagination-numbers {
              margin: 0;
              justify-content: center;
            }

            .pagination-jump {
              flex-direction: column;
              gap: 8px;
            }

            .pagination-input {
              width: 60px;
            }

            /* Breadcrumbs móvil */
            .breadcrumbs-section-header .breadcrumb-list {
              font-size: 0.8rem;
            }
          }
        `}</style>
      </main>
    </>
  );
};

// Función para calcular números de paginación con puntos suspensivos
const getPaginationNumbers = (current, total) => {
  const delta = 2; // Números a mostrar a cada lado de la página actual
  const range = [];
  const rangeWithDots = [];

  // Casos especiales para pocas páginas
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      range.push(i);
    }
    return range;
  }

  // Siempre mostrar primera página
  rangeWithDots.push(1);

  // Calcular el rango alrededor de la página actual
  const start = Math.max(2, current - delta);
  const end = Math.min(total - 1, current + delta);

  // Agregar puntos suspensivos después de 1 si es necesario
  if (start > 2) {
    rangeWithDots.push("...");
  }

  // Agregar números del rango
  for (let i = start; i <= end; i++) {
    rangeWithDots.push(i);
  }

  // Agregar puntos suspensivos antes de la última página si es necesario
  if (end < total - 1) {
    rangeWithDots.push("...");
  }

  // Siempre mostrar última página (si no es la primera)
  if (total > 1) {
    rangeWithDots.push(total);
  }

  return rangeWithDots;
};

// Función auxiliar para obtener colores de categorías
const getCategoryColor = (slug) => {
  const colors = {
    politica: "#dc2626",
    economia: "#059669",
    deportes: "#ea580c",
    cultura: "#7c3aed",
    tecnologia: "#0891b2",
    salud: "#c2410c",
    educacion: "#4338ca",
    "medio-ambiente": "#16a34a",
    internacional: "#1f2937",
    nacional: "#991b1b",
    noticias: "#3b82f6",
    sociedad: "#7c2d12",
    seguridad: "#b91c1c",
  };
  return colors[slug] || "#3b82f6";
};

// Función para obtener iconos de categorías
const getCategoryIcon = (slug) => {
  const icons = {
    politica: "fas fa-landmark",
    economia: "fas fa-chart-line",
    deportes: "fas fa-futbol",
    cultura: "fas fa-palette",
    tecnologia: "fas fa-laptop",
    salud: "fas fa-heartbeat",
    educacion: "fas fa-graduation-cap",
    "medio-ambiente": "fas fa-leaf",
    internacional: "fas fa-globe-americas",
    nacional: "fas fa-flag",
    noticias: "fas fa-newspaper",
    sociedad: "fas fa-users",
    seguridad: "fas fa-shield-alt",
    entretenimiento: "fas fa-film",
    ciencia: "fas fa-microscope",
    opinion: "fas fa-comments",
  };
  return icons[slug] || "fas fa-newspaper";
};
